import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { enqueueSnackbar } from './utils/snackbar';
import {
  createOneMinutePaperEntry,
  fetchOneMinutePaperReservaState,
  OneMinutePaperReservaState,
} from '../api/oneMinutePaper';

type Props = {
  open: boolean;
  reservaId?: number;
};

const MAX_FILES = 5;

const fileFingerprint = (f: File) => `${f.name}-${f.size}-${f.lastModified}`;

export default function OneMinutePaperSection({ open, reservaId }: Props) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<OneMinutePaperReservaState | null>(null);
  const [notaByAlumno, setNotaByAlumno] = useState<Record<number, string>>({});
  const [filesByAlumno, setFilesByAlumno] = useState<Record<number, File[]>>({});
  const [savingAlumno, setSavingAlumno] = useState<number | null>(null);

  const canFetch = open && !!reservaId;

  const load = async () => {
    if (!reservaId) return;
    try {
      setLoading(true);
      const response = await fetchOneMinutePaperReservaState(reservaId);
      setData(response);
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ??
        error?.message ??
        'No se pudo cargar el ejercicio de inicio de clase';
      enqueueSnackbar(String(msg), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canFetch) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, reservaId]);

  useEffect(() => {
    if (!data?.rows?.length) return;

    setNotaByAlumno((prev) => {
      const next = { ...prev };
      for (const row of data.rows) {
        if (typeof next[row.alumnoId] === 'undefined') {
          next[row.alumnoId] = row.nota != null ? String(row.nota) : '';
        }
      }
      return next;
    });
  }, [data]);

  const pendientes = useMemo(() => {
    if (!data) return 0;
    return data.rows.filter((r) => !r.hasEntry).length;
  }, [data]);

  const onSelectFiles = (alumnoId: number, filesList: FileList | null) => {
    const incoming = Array.from(filesList ?? []);
    if (!incoming.length) return;

    setFilesByAlumno((prev) => {
      const current = prev[alumnoId] ?? [];
      const currentKeys = new Set(current.map(fileFingerprint));

      const merged = [...current];
      for (const file of incoming) {
        const key = fileFingerprint(file);
        if (!currentKeys.has(key)) {
          merged.push(file);
          currentKeys.add(key);
        }
      }

      const next = merged.slice(0, MAX_FILES);
      if (merged.length > MAX_FILES) {
        enqueueSnackbar(`Solo se permiten ${MAX_FILES} imágenes`, { variant: 'warning' });
      }

      return { ...prev, [alumnoId]: next };
    });
  };

  const onSave = async (alumnoId: number) => {
    if (!reservaId || !data) return;

    const notaStr = String(notaByAlumno[alumnoId] ?? '').trim();
    const nota = Number(notaStr.replace(',', '.'));
    if (!Number.isFinite(nota) || nota < 0 || nota > 10) {
      enqueueSnackbar('La nota debe estar entre 0 y 10', { variant: 'error' });
      return;
    }

    const files = filesByAlumno[alumnoId] ?? [];
    if (files.length === 0) {
      enqueueSnackbar('Debes adjuntar al menos una imagen', { variant: 'error' });
      return;
    }

    if (files.length > MAX_FILES) {
      enqueueSnackbar(`Solo se permiten ${MAX_FILES} imágenes`, { variant: 'error' });
      return;
    }

    try {
      setSavingAlumno(alumnoId);
      await createOneMinutePaperEntry({
        reservaId,
        alumnoId,
        nota,
        files,
      });

      enqueueSnackbar('Ejercicio de inicio de clase registrado correctamente', {
        variant: 'success',
      });
      setFilesByAlumno((prev) => ({ ...prev, [alumnoId]: [] }));
      await load();
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ??
        error?.message ??
        'No se pudo guardar el ejercicio de inicio de clase';
      enqueueSnackbar(String(msg), { variant: 'error' });
    } finally {
      setSavingAlumno(null);
    }
  };

  if (!open || !reservaId) return null;

  return (
    <Box
      sx={{
        mt: 1.5,
        p: 1.5,
        border: '1px dashed #ccc',
        borderRadius: 1,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
        <Typography variant="subtitle2">Ejercicio de inicio de clase</Typography>
        {data && (
          <Chip
            size="small"
            color={pendientes > 0 ? 'warning' : 'success'}
            label={pendientes > 0 ? `${pendientes} pendientes` : 'Completado'}
          />
        )}
      </Stack>

      {loading && <CircularProgress size={20} />}

      {data?.deadlineAt && (
        <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
          Plazo de registro: {dayjs(data.deadlineAt).format('DD/MM/YYYY HH:mm')}
        </Typography>
      )}

      {data?.reason && (
        <Alert severity={data.applicable ? 'warning' : 'info'} sx={{ mb: 1 }}>
          {data.reason}
        </Alert>
      )}

      {data &&
        data.rows.map((row) => {
          const isSaving = savingAlumno === row.alumnoId;
          const selectedFiles = filesByAlumno[row.alumnoId] ?? [];

          return (
            <Box
              key={row.alumnoId}
              sx={{
                p: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                mb: 1,
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', md: 'center' }}
              >
                <Typography sx={{ minWidth: 180 }}>{row.alumno}</Typography>

                {row.hasEntry ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" color="success" label={`Nota: ${row.nota ?? '—'}`} />
                    <Chip size="small" label={`${row.evidencias.length} evidencias`} />
                  </Stack>
                ) : (
                  <>
                    <TextField
                      size="small"
                      label="Nota"
                      value={notaByAlumno[row.alumnoId] ?? ''}
                      onChange={(e) =>
                        setNotaByAlumno((prev) => ({
                          ...prev,
                          [row.alumnoId]: e.target.value,
                        }))
                      }
                      inputProps={{ inputMode: 'decimal' }}
                      sx={{ width: 110 }}
                    />

                    <Button component="label" variant="outlined" size="small">
                      Adjuntar fotos ({selectedFiles.length}/{MAX_FILES})
                      <input
                        hidden
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => {
                          onSelectFiles(row.alumnoId, e.target.files);
                          e.currentTarget.value = '';
                        }}
                      />
                    </Button>

                    {selectedFiles.length > 0 && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={() =>
                          setFilesByAlumno((prev) => ({
                            ...prev,
                            [row.alumnoId]: [],
                          }))
                        }
                      >
                        Limpiar fotos
                      </Button>
                    )}

                    <Button
                      variant="contained"
                      size="small"
                      disabled={isSaving || !data.canUpload}
                      onClick={() => onSave(row.alumnoId)}
                      sx={{
                        backgroundColor: '#21a598',
                        '&:hover': { backgroundColor: '#1a8578' },
                        boxShadow: 'none',
                      }}
                    >
                      {isSaving ? 'Guardando…' : 'Guardar'}
                    </Button>
                  </>
                )}
              </Stack>

              {!row.hasEntry && (
                <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Puedes pulsar "Adjuntar fotos" varias veces para acumular imágenes hasta{' '}
                  {MAX_FILES}.
                </Typography>
              )}
            </Box>
          );
        })}
    </Box>
  );
}
